import React, { createContext, useCallback, useEffect, useReducer } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'utils/axios';

import { partnerLogin, PartnerUser } from 'api/PartnerAuthApi';
import { PARTNER_TOKEN_KEY, PARTNER_USER_KEY, PARTNER_LOGOUT_EVENT } from 'utils/sessionCoordinator';
import Sockets from 'services/SocketService';
import { updateModelAttributes } from 'services/SocketModelUpdate';

export type EnclosureStatus = 'loading' | 'valid' | 'invalid';

export interface PartnerAuthState {
    isLoggedIn: boolean;
    isInitialized: boolean;
    user: PartnerUser | null;
    enclosureId?: string | number;
    enclosureName?: string;
    groupName?: string;
    enclosureStatus: EnclosureStatus;
    mismatchError?: string | null;
}

export interface PartnerAuthContextValue extends PartnerAuthState {
    login: (username: string, password: string, mpId: string) => Promise<{ error?: string }>;
    logout: () => void;
    clearMismatchError: () => void;
    setMismatchError: (message: string | null) => void;
}

const initialState: PartnerAuthState = {
    isLoggedIn: false,
    isInitialized: false,
    user: null,
    enclosureId: undefined,
    enclosureName: undefined,
    enclosureStatus: 'loading',
    mismatchError: null
};

type Action =
    | { type: 'INIT_DONE'; user: PartnerUser | null }
    | { type: 'LOGIN'; user: PartnerUser }
    | { type: 'LOGOUT' }
    | {
          type: 'SET_ENCLOSURE';
          enclosureId: string | number | undefined;
          enclosureName?: string;
          groupName?: string;
          status: EnclosureStatus;
      }
    | { type: 'SET_MISMATCH_ERROR'; message: string | null };

const reducer = (state: PartnerAuthState, action: Action): PartnerAuthState => {
    switch (action.type) {
        case 'INIT_DONE':
            return { ...state, isInitialized: true, isLoggedIn: !!action.user, user: action.user };
        case 'LOGIN':
            return { ...state, isInitialized: true, isLoggedIn: true, user: action.user, mismatchError: null };
        case 'LOGOUT':
            return { ...state, isInitialized: true, isLoggedIn: false, user: null };
        case 'SET_ENCLOSURE':
            return {
                ...state,
                enclosureId: action.enclosureId,
                enclosureName: action.enclosureName,
                enclosureStatus: action.status,
                groupName: action.groupName
            };
        case 'SET_MISMATCH_ERROR':
            return { ...state, mismatchError: action.message };
        default:
            return state;
    }
};

const sameEnclosure = (a: any, b: any) => a != null && b != null && String(a) === String(b);

const PartnerAuthContext = createContext<PartnerAuthContextValue | null>(null);

export const PartnerAuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const { mpId } = useParams();
    const navigate = useNavigate();
    const IDLE_TIMEOUT_KEY = 'mp_lastBetActivity';

    useEffect(() => {
        const token = localStorage.getItem(PARTNER_TOKEN_KEY);
        if (!token) {
            dispatch({ type: 'INIT_DONE', user: null });
            return;
        }
        const stored = localStorage.getItem(PARTNER_USER_KEY);
        let user: PartnerUser | null = null;
        if (stored) {
            try {
                user = JSON.parse(stored);
            } catch {
                user = null;
            }
        }
        // Migration: stored objects from before the login response was
        // wired up may be missing enclosureId / operatorId. Drop them so the
        // user has to sign in again, and we get a fresh, complete profile.
        if (user && user.enclosureId == null) {
            localStorage.removeItem(PARTNER_TOKEN_KEY);
            localStorage.removeItem(PARTNER_USER_KEY);
            localStorage.removeItem(IDLE_TIMEOUT_KEY);
            user = null;
        }
        // Re-open the socket for the restored session so the bet/race pages
        // receive live updates after a refresh.
        if (user?.userId) {
            Sockets.setSocketForUser(String(user.userId), updateModelAttributes);
        }
        dispatch({ type: 'INIT_DONE', user });
    }, []);

    // Fetch the enclosure for the current marketing-partner. Re-runs if mpId
    // changes (e.g. navigation between partners), so the value is always tied
    // to the URL. The API returns `{ body: { id, name, ... } }` and we use
    // `body.id` as the enclosureId.
    useEffect(() => {
        if (!mpId) return;
        let cancelled = false;
        dispatch({
            type: 'SET_ENCLOSURE',
            enclosureId: undefined,
            enclosureName: undefined,
            groupName: undefined,
            status: 'loading'
        });
        const fetchEnclosure = async () => {
            try {
                const gameUrl = import.meta.env.VITE_APP_GAME_URL;
                const response = await axios.get(`${gameUrl}/lbtsoenclosure/${mpId}`);
                const body = response.data?.body;

                // body === null → marketing-partner code didn't resolve to a
                // known enclosure. Block the page with an error message and
                // force the user out if they happened to be signed in.
                if (!body) {
                    if (cancelled) return;
                    dispatch({
                        type: 'SET_ENCLOSURE',
                        enclosureId: undefined,
                        enclosureName: undefined,
                        groupName: undefined,
                        status: 'invalid'
                    });
                    dispatch({
                        type: 'SET_MISMATCH_ERROR',
                        message: `Invalid marketing partner "${mpId}". This enclosure does not exist.`
                    });
                    if (Sockets.instance) Sockets.instance.unsubscribe();
                    localStorage.removeItem(PARTNER_TOKEN_KEY);
                    localStorage.removeItem(PARTNER_USER_KEY);
                    dispatch({ type: 'LOGOUT' });
                    return;
                }

                if (!cancelled) {
                    dispatch({
                        type: 'SET_ENCLOSURE',
                        enclosureId: body.id,
                        enclosureName: body.name,
                        groupName: body.groupName,
                        status: 'valid'
                    });
                }
            } catch (err) {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error('[partner] failed to fetch enclosure', err);
                dispatch({
                    type: 'SET_ENCLOSURE',
                    enclosureId: undefined,
                    enclosureName: undefined,
                    groupName: undefined,
                    status: 'invalid'
                });
                dispatch({
                    type: 'SET_MISMATCH_ERROR',
                    message: `Unable to load marketing partner "${mpId}". Please try again later.`
                });
            }
        };
        fetchEnclosure();
        return () => {
            cancelled = true;
        };
    }, [mpId]);

    useEffect(() => {
        const onPartnerLogout = () => dispatch({ type: 'LOGOUT' });
        window.addEventListener(PARTNER_LOGOUT_EVENT, onPartnerLogout);
        return () => window.removeEventListener(PARTNER_LOGOUT_EVENT, onPartnerLogout);
    }, []);

    // Watch for enclosure / user mismatches. Fires when the user navigates
    // to a partner URL whose enclosure doesn't match the one their account
    // is bound to — log them out and surface a message.
    useEffect(() => {
        if (!state.isLoggedIn) return;
        if (state.user?.enclosureId == null || state.enclosureId == null) return;
        if (sameEnclosure(state.user.enclosureId, state.enclosureId)) return;

        if (Sockets.instance) Sockets.instance.unsubscribe();
        localStorage.removeItem(PARTNER_TOKEN_KEY);
        localStorage.removeItem(PARTNER_USER_KEY);
        dispatch({ type: 'LOGOUT' });
        dispatch({
            type: 'SET_MISMATCH_ERROR',
            message: `This enclosure (${state.enclosureName ?? state.enclosureId}) doesn't match your account's enclosure. You have been signed out.`
        });
    }, [state.enclosureId, state.user?.enclosureId, state.isLoggedIn, state.enclosureName]);

    const login = useCallback(
        async (username: string, password: string, mpIdArg: string) => {
            try {
                const { token, user } = await partnerLogin({
                    username,
                    password,
                    mpId: mpIdArg,
                    enclosureId: state.enclosureId
                });

                // Reject the login if the account's enclosure doesn't match
                // the enclosure for this partner URL.
                if (state.enclosureId != null && user.enclosureId != null && !sameEnclosure(state.enclosureId, user.enclosureId)) {
                    return {
                        error: `This account belongs to a different enclosure (id ${user.enclosureId}). You can only sign in on its own marketing-partner URL.`
                    };
                }

                localStorage.setItem(PARTNER_TOKEN_KEY, token);
                localStorage.setItem(PARTNER_USER_KEY, JSON.stringify(user));
                // Store login time / initial activity time
                localStorage.setItem(IDLE_TIMEOUT_KEY, Date.now().toString());
                if (user.userId) {
                    Sockets.setSocketForUser(String(user.username), updateModelAttributes);
                }
                dispatch({ type: 'LOGIN', user });
                return {};
            } catch (err: any) {
                return { error: err?.message || 'Login failed' };
            }
        },
        [state.enclosureId]
    );

    const logout = useCallback(() => {
        if (Sockets.instance) {
            Sockets.instance.unsubscribe();
        }
        localStorage.removeItem(PARTNER_TOKEN_KEY);
        localStorage.removeItem(PARTNER_USER_KEY);
         localStorage.removeItem(IDLE_TIMEOUT_KEY);
        dispatch({ type: 'LOGOUT' });
        // Return to the partner home URL (drop any race/deep path so the user
        // lands on /mp/:mpId, not e.g. /mp/:mpId/fixedracing/3152/8/tote).
        if (mpId) navigate(`/mp/${mpId}`, { replace: true });
    }, [mpId, navigate]);

    const clearMismatchError = useCallback(() => {
        dispatch({ type: 'SET_MISMATCH_ERROR', message: null });
    }, []);

    const setMismatchError = useCallback((message: string | null) => {
        dispatch({
            type: 'SET_MISMATCH_ERROR',
            message
        });
    }, []);

    return (
        <PartnerAuthContext.Provider value={{ ...state, login, logout, clearMismatchError, setMismatchError }}>
            {children}
        </PartnerAuthContext.Provider>
    );
};

export default PartnerAuthContext;
